import { defaultDateStyle, defaultLocaleForDate, defaultTimeZone } from '@/config/date'

export interface FormatDateTimeOptions {
  // Inputs for formatDateTime().

  date: Date | string
  // Date/time as Date or ISO string.

  locale?: string
  // Locale for Intl formatting. Default from config.

  dateStyle?: 'long' | 'short' | 'medium'
  // Intl dateStyle for the date part. Default from config.

  timeStyle?: 'short' | 'medium' | 'long'
  // Intl timeStyle for the time part.

  timeZone?: string
  // IANA timezone for display. Undefined = local. Default from config.
}

export interface FormatDateTimeResult {
  // Split strings for stacked date/time display.

  formattedDate: string
  // Localized calendar date.

  formattedTime: string
  // Localized time of day.
}

function toDate(date: Date | string): Date {
  return typeof date === 'string' ? new Date(date) : date
}

/**
 * Format a date/time into separate date and time strings for display (e.g. date above, time below).
 */
export function formatDateTime(options: FormatDateTimeOptions): FormatDateTimeResult {
  const {
    date,
    locale = defaultLocaleForDate.value,
    dateStyle = defaultDateStyle,
    timeStyle = 'short',
    timeZone = defaultTimeZone,
  } = options

  const d = toDate(date)
  const baseOptions: Intl.DateTimeFormatOptions = {
    timeZone: timeZone ?? undefined,
  }

  const formattedDate = new Intl.DateTimeFormat(locale, {
    ...baseOptions,
    dateStyle,
  }).format(d)

  const formattedTime = new Intl.DateTimeFormat(locale, {
    ...baseOptions,
    timeStyle,
  }).format(d)

  return { formattedDate, formattedTime }
}
